import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, eur, catLabel, gr } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, MapTrifold, Eye } from "@phosphor-icons/react";
import BoatDetailsDialog from "@/components/BoatDetailsDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function SlotsGrid() {
  const navigate = useNavigate();
  const { data: slots = [], isLoading } = useQuery({
    queryKey: ["slots"],
    queryFn: async () => (await api.get("/slots")).data,
    refetchInterval: 5000,
  });
  const { data: boats = [] } = useQuery({
    queryKey: ["boats"],
    queryFn: async () => (await api.get("/boats")).data,
  });

  const occupied = slots.filter((s) => s.occupied).length;

  const groups = [];
  const idx = {};
  slots.forEach((s) => {
    const key = s.category_name || "—";
    if (idx[key] === undefined) { idx[key] = groups.length; groups.push({ name: key, items: [] }); }
    groups[idx[key]].items.push(s);
  });

  const [sort, setSort] = useState("default");
  const [historySlot, setHistorySlot] = useState(null);
  const [finBoatId, setFinBoatId] = useState(null);
  const [availOpen, setAvailOpen] = useState(false);
  const [availForm, setAvailForm] = useState({ length: "", start: "", end: "" });
  const [availResult, setAvailResult] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const runAvailability = async () => {
    setAvailLoading(true); setAvailResult(null);
    try {
      const params = new URLSearchParams();
      if (availForm.length) params.set("length", availForm.length);
      if (availForm.start) params.set("start_date", availForm.start);
      if (availForm.end) params.set("end_date", availForm.end);
      const res = (await api.get(`/availability?${params.toString()}`)).data;
      setAvailResult(res);
    } catch (e) { toast.error("Σφάλμα ελέγχου"); } finally { setAvailLoading(false); }
  };
  const sortItems = (items) => {
    const arr = [...items];
    if (sort === "empty") arr.sort((a, b) => (a.occupied ? 1 : 0) - (b.occupied ? 1 : 0));
    else if (sort === "occupied") arr.sort((a, b) => (b.occupied ? 1 : 0) - (a.occupied ? 1 : 0));
    else if (sort === "exit") arr.sort((a, b) => {
      const ea = a.boat?.end_date || ""; const eb = b.boat?.end_date || "";
      if (!ea && !eb) return 0;
      if (!ea) return 1;
      if (!eb) return -1;
      return ea < eb ? -1 : 1;
    });
    return arr;
  };

  return (
    <div>
      <PageHeader
        title="Χάρτης θέσεων"
        subtitle={`${occupied} / ${slots.length} κατειλημμένες`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate("/plot-view")} className="rounded-sm" data-testid="slots-plotview-btn">
              <Eye size={18} weight="bold" /> Προβολή κάτοψης θέσεων
            </Button>
            <Button variant="outline" onClick={() => navigate("/plot")} className="rounded-sm" data-testid="slots-plot-btn">
              <MapTrifold size={18} weight="bold" /> Σχεδίαση κάτοψης θέσεων
            </Button>
            <Button variant="outline" onClick={() => { setAvailForm({ length: "", start: "", end: "" }); setAvailResult(null); setAvailOpen(true); }} className="rounded-sm" data-testid="slots-availability-btn">
              <MagnifyingGlass size={18} weight="bold" /> Έλεγχος διαθεσιμότητας
            </Button>
            <Button onClick={() => navigate("/boats?new=1")} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="slots-new-boat-btn">
              <Plus size={18} weight="bold" /> Νέο σκάφος
            </Button>
          </div>
        }
      />
      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-5 text-xs">
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-sm border border-[#0055FF] bg-[#EFF6FF]" /> Κενή
            </div>
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-sm bg-[#46648B]" /> Κατειλημμένη
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Ταξινόμηση:</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-56 rounded-sm" data-testid="slots-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Προεπιλογή (αριθμός θέσης)</SelectItem>
                <SelectItem value="empty">Κενές θέσεις</SelectItem>
                <SelectItem value="occupied">Κατειλημμένες θέσεις</SelectItem>
                <SelectItem value="exit">Χρόνος εξόδου</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="text-muted-foreground">Φόρτωση...</div>
        ) : groups.length === 0 ? (
          <div className="rounded-sm border border-dashed border-border p-10 text-center" data-testid="slots-empty">
            <div className="text-sm text-muted-foreground">Δεν έχουν οριστεί τα στοιχεία της εγκατάστασής σας και η τιμολόγηση.</div>
            <Button className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => navigate("/setup")} data-testid="slots-setup-btn">Ρύθμιση στοιχείων εγκατάστασης</Button>
          </div>
        ) : (
          <TooltipProvider delayDuration={80}>
            <div className="space-y-6">
              {groups.map((g) => {
                const occ = g.items.filter((s) => s.occupied).length;
                return (
                  <div key={g.name} data-testid={`slots-category-${g.name}`}>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="font-head text-base font-bold text-[#234E7D]">{catLabel(g.name)}</h3>
                      <span className="text-xs text-muted-foreground">{occ} / {g.items.length} κατειλημμένες</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-6 lg:grid-cols-10" data-testid="slots-grid">
                      {sortItems(g.items).map((s) => (
                        <Tooltip key={s.id}>
                          <TooltipTrigger asChild>
                            <div
                              data-testid={`slot-${s.number}`}
                              onClick={() => setHistorySlot(s)}
                              className={`flex aspect-square cursor-pointer flex-col items-center justify-center rounded-sm border p-2 text-center transition-colors duration-150 ${
                                s.occupied
                                  ? "border-[#46648B] bg-[#46648B] text-white hover:bg-[#234E7D]"
                                  : "border-[#0055FF]/40 bg-[#EFF6FF] text-[#234E7D] hover:border-[#0055FF]"
                              }`}
                            >
                              <span className="font-head text-sm font-bold leading-tight break-words">{s.label ? s.label : `#${s.number}`}</span>
                              <span className={`text-[10px] ${s.occupied ? "text-white/60" : "text-muted-foreground"}`}>
                                έως {s.max_length}m
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="rounded-sm border-none bg-[#234E7D] text-white">
                            <div className="text-xs">
                              <div className="font-semibold">Θέση {s.label ? s.label : `#${s.number}`} — {catLabel(s.category_name)}</div>
                              {s.occupied ? (
                                <div className="mt-1 space-y-0.5">
                                  <div>Σκάφος: {s.boat.name}</div>
                                  <div>Ιδιοκτήτης: {s.boat.owner}</div>
                                  <div>Μήκος: {s.boat.length}m</div>
                                  <div>Οφειλή: {eur(s.boat.amount)}</div>
                                </div>
                              ) : (
                                <div className="mt-1 text-white/70">Κενή (έως {s.max_length}m)</div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>
        )}
      </div>

      <Dialog open={!!historySlot} onOpenChange={(o) => !o && setHistorySlot(null)}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="slot-history-dialog">
          <DialogHeader>
            <DialogTitle>
              Στοιχεία θέσης {historySlot?.label ? historySlot.label : `#${historySlot?.number}`} — {historySlot ? catLabel(historySlot.category_name) : ""}
            </DialogTitle>
          </DialogHeader>
          {historySlot && !historySlot.occupied && (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="slot-history-empty">
              Η θέση είναι κενή. Καμία καταχώρηση.
            </div>
          )}
          {historySlot && historySlot.occupied && (() => {
            const b = historySlot.boat;
            const items = [
              { date: b.start_date, label: "Χρέωση παραμονής", amount: b.amount, type: "charge" },
              ...(b.charges || []).map((c) => ({ date: c.date, label: c.description, amount: c.amount, type: "charge" })),
              ...(b.payments || []).map((p) => ({ date: p.date, label: p.note || "Πληρωμή", amount: p.amount, type: "payment" })),
            ].sort((x, y) => (x.date || "") < (y.date || "") ? -1 : 1);
            const charged = b.amount + (b.charges || []).reduce((s, c) => s + Number(c.amount || 0), 0);
            const paid = (b.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
            return (
              <div className="space-y-4">
                <div className="rounded-sm bg-secondary/50 p-3 text-sm">
                  <div className="font-semibold">{b.name} — {b.owner}</div>
                  {b.registry_no && <div className="text-xs text-muted-foreground">{b.registry_no}</div>}
                  <div className="mt-1 text-xs text-muted-foreground">Παραμονή: {gr(b.start_date)} → {gr(b.end_date)}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 rounded-sm bg-[#10B981] hover:bg-[#0e9d70]"
                    data-testid="slot-financials-btn"
                    onClick={() => { setFinBoatId(b.id); setHistorySlot(null); }}
                  >
                    Οικονομικά στοιχεία
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 rounded-sm"
                    data-testid="slot-edit-boat-btn"
                    onClick={() => { setHistorySlot(null); navigate(`/boats?edit=${b.id}`); }}
                  >
                    Επεξεργασία στοιχείων
                  </Button>
                </div>
                <div className="space-y-1" data-testid="slot-history-list">
                  {items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between border-b border-border py-1.5 text-sm last:border-0">
                      <div>
                        <span className="text-xs text-muted-foreground tabular-nums">{gr(it.date)}</span>
                        <span className="ml-2">{it.label}</span>
                      </div>
                      <span className={`font-medium tabular-nums ${it.type === "payment" ? "text-[#10B981]" : "text-[#234E7D]"}`}>
                        {it.type === "payment" ? "−" : "+"}{eur(it.amount)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 border-t border-border pt-2 text-sm font-semibold">
                  <div className="flex justify-between">
                    <span>Χρέωση έως σήμερα</span>
                    <span className="tabular-nums">{eur(b.charged_to_date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Υπόλοιπο στη λήξη</span>
                    <span className="tabular-nums">{eur(b.balance_to_end)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <BoatDetailsDialog
        boat={boats.find((b) => b.id === finBoatId)}
        open={!!finBoatId}
        onOpenChange={(o) => !o && setFinBoatId(null)}
      />

      <Dialog open={availOpen} onOpenChange={setAvailOpen}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="availability-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Έλεγχος διαθεσιμότητας</DialogTitle>
            <DialogDescription>Δείτε ποιες θέσεις είναι διαθέσιμες για μήκος και περίοδο.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Μήκος σκάφους (μ)</Label>
              <Input type="number" min="0" step="0.1" value={availForm.length} onChange={(e) => setAvailForm({ ...availForm, length: e.target.value })} className="mt-1 rounded-sm" placeholder="π.χ. 10" data-testid="availability-length" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Από</Label>
                <DateField value={availForm.start} onChange={(v) => setAvailForm({ ...availForm, start: v })} testId="availability-start" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Έως</Label>
                <DateField value={availForm.end} onChange={(v) => setAvailForm({ ...availForm, end: v })} testId="availability-end" />
              </div>
            </div>
            <Button onClick={runAvailability} disabled={availLoading} className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="availability-run">
              {availLoading ? "Έλεγχος..." : "Έλεγχος"}
            </Button>
            {availResult && (
              <div className="rounded-sm border border-border p-3" data-testid="availability-result">
                <div className="text-sm font-semibold text-[#234E7D]">{availResult.available} από {availResult.total} διαθέσιμες</div>
                {availResult.available === 0 ? (
                  <p className="mt-1 text-sm text-muted-foreground">Καμία διαθέσιμη θέση για τα κριτήρια.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {availResult.categories.map((c, i) => (
                      <div key={i}>
                        <div className="text-xs font-semibold text-muted-foreground">{catLabel(c.category)}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {c.slots.map((n) => (
                            <button
                              key={n}
                              onClick={() => { setAvailOpen(false); navigate(`/boats?new=1&slot=${n}&length=${availForm.length || ""}&start=${availForm.start || ""}&end=${availForm.end || ""}`); }}
                              className="rounded-sm border border-[#0055FF]/40 bg-[#EFF6FF] px-2 py-0.5 text-xs font-medium text-[#234E7D] transition-colors hover:border-[#0055FF] hover:bg-[#0055FF] hover:text-white"
                              data-testid={`availability-slot-${n}`}
                              title="Νέο σκάφος σε αυτή τη θέση"
                            >
                              #{n}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
